import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import getScrollParent from "./getScrollParent";

// Re-render only when the viewport moves by this many pixels.
const SCROLL_STEP = 120;

function readViewport(container, scrollParent) {
  const rect = container.getBoundingClientRect();
  const viewportTop = scrollParent ? scrollParent.getBoundingClientRect().top : 0;
  const viewportHeight = scrollParent ? scrollParent.clientHeight : window.innerHeight;
  const top = viewportTop - rect.top;
  return {
    top: Math.floor(top / SCROLL_STEP) * SCROLL_STEP,
    height: Math.ceil(viewportHeight / SCROLL_STEP) * SCROLL_STEP,
  };
}

/**
 * Windowed masonry: places items into the shortest column, measures their real heights with
 * a ResizeObserver, and returns only the items within `overscan` px of the viewport. Items
 * off screen are unmounted; absolute positioning keeps the scroll position steady.
 * An item's column is fixed once assigned, so late measurements never reshuffle the grid.
 */
export default function useWindowedMasonry({
  items,
  getKey,
  columnCount,
  gap = 16,
  estimateHeight,
  overscan = 1200,
}) {
  const [container, setContainer] = useState(null);
  const heightsRef = useRef(new Map());
  const columnsRef = useRef({ count: columnCount, byKey: new Map() });
  const [measureVersion, setMeasureVersion] = useState(0);
  const [viewport, setViewport] = useState({ top: 0, height: 1000 });

  const layout = useMemo(() => {
    if (columnsRef.current.count !== columnCount) {
      columnsRef.current = { count: columnCount, byKey: new Map() };
    }
    const columns = columnsRef.current.byKey;
    const columnHeights = new Array(columnCount).fill(0);
    const placed = items.map((item) => {
      const key = getKey(item);
      const height = heightsRef.current.get(key) ?? estimateHeight(item);
      let column = columns.get(key);
      if (column === undefined) {
        column = columnHeights.indexOf(Math.min(...columnHeights));
        columns.set(key, column);
      }
      const top = columnHeights[column];
      columnHeights[column] += height + gap;
      return { item, key, column, top, height };
    });
    return { placed, totalHeight: Math.max(0, Math.max(...columnHeights) - gap) };
    // measureVersion re-runs layout after heights change.
  }, [items, columnCount, gap, getKey, estimateHeight, measureVersion]);

  const visible = useMemo(() => {
    const start = viewport.top - overscan;
    const end = viewport.top + viewport.height + overscan;
    return layout.placed.filter(({ top, height }) => top + height >= start && top <= end);
  }, [layout, viewport, overscan]);

  // Track the viewport relative to the grid, throttled to animation frames.
  useLayoutEffect(() => {
    if (!container) return undefined;
    const scrollParent = getScrollParent(container);
    const target = scrollParent || window;
    let frame = 0;
    const update = () => {
      frame = 0;
      const next = readViewport(container, scrollParent);
      setViewport((prev) => (prev.top === next.top && prev.height === next.height ? prev : next));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    target.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(frame);
      target.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [container]);

  // One shared observer records real heights; layout updates are batched per frame.
  const observerRef = useRef(null);
  const nodesRef = useRef(new Map());
  useEffect(() => {
    let frame = 0;
    const observer = new ResizeObserver((entries) => {
      let changed = false;
      entries.forEach((entry) => {
        if (!entry.target.isConnected) return;
        const key = entry.target.dataset.windowKey;
        const height = Math.round(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height);
        if (key && Math.abs((heightsRef.current.get(key) ?? -1) - height) > 1) {
          heightsRef.current.set(key, height);
          changed = true;
        }
      });
      if (changed && !frame) {
        frame = requestAnimationFrame(() => {
          frame = 0;
          setMeasureVersion((v) => v + 1);
        });
      }
    });
    observerRef.current = observer;
    nodesRef.current.forEach((node) => observer.observe(node));
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      observerRef.current = null;
    };
  }, []);

  // Stable ref callback per key, so re-renders don't detach and re-observe every node.
  const refCallbacksRef = useRef(new Map());
  const measureRef = useCallback((key) => {
    let callback = refCallbacksRef.current.get(key);
    if (!callback) {
      callback = (node) => {
        const prev = nodesRef.current.get(key);
        if (prev && prev !== node) observerRef.current?.unobserve(prev);
        if (node) {
          node.dataset.windowKey = key;
          nodesRef.current.set(key, node);
          observerRef.current?.observe(node);
        } else {
          nodesRef.current.delete(key);
          refCallbacksRef.current.delete(key);
        }
      };
      refCallbacksRef.current.set(key, callback);
    }
    return callback;
  }, []);

  const getItemStyle = useCallback(
    ({ column, top }) => ({
      position: "absolute",
      top,
      left: `calc(${column} * ((100% - ${(columnCount - 1) * gap}px) / ${columnCount} + ${gap}px))`,
      width: `calc((100% - ${(columnCount - 1) * gap}px) / ${columnCount})`,
    }),
    [columnCount, gap]
  );

  return {
    containerRef: setContainer,
    containerStyle: { position: "relative", height: layout.totalHeight },
    visible,
    measureRef,
    getItemStyle,
  };
}
